import { useCallback, useRef, useState, useEffect } from "react";

interface UseSortableItemsProps<T> {
  value: T[];
  onChange: (newValue: T[], oldIndex: number, newIndex: number) => void;
  direction?: "vertical" | "horizontal";
}

interface SortableItemProps {
  style: React.CSSProperties;
  "data-index": number;
  "data-sortable": boolean;
  draggable: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
}

interface ScrollState {
  scaleX: number;
  scaleY: number;
  frame: number | null;
}

export function useSortableItems<T>({
  value,
  onChange,
  direction = "vertical"
}: UseSortableItemsProps<T>) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [originalDragIndex, setOriginalDragIndex] = useState<number | null>(null);
  const [tempValue, setTempValue] = useState<T[]>(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollState = useRef<ScrollState>({
    scaleX: 0,
    scaleY: 0,
    frame: null
  });

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const BUFFER = 150;
  const STRENGTH_MULTIPLIER = 30;

  const calculateStrength = useCallback((containerRect: DOMRect, mouseX: number) => {
    const buffer = Math.min(containerRect.width / 2, BUFFER);

    if (mouseX < containerRect.left + buffer) {
      return (mouseX - containerRect.left - buffer) / buffer;
    } else if (mouseX > containerRect.right - buffer) {
      return -(containerRect.right - mouseX - buffer) / buffer;
    }
    return 0;
  }, []);

  const startScrolling = useCallback(() => {
    const tick = () => {
      const { scaleX } = scrollState.current;
      const container = containerRef.current;

      if (!container || !scaleX) {
        stopScrolling();
        return;
      }

      const { scrollLeft, scrollWidth, clientWidth } = container;

      // Calculate new scroll position
      const newLeft = Math.max(
        0,
        Math.min(
          scrollWidth - clientWidth,
          scrollLeft + scaleX * STRENGTH_MULTIPLIER
        )
      );

      // Apply scroll
      container.scrollLeft = newLeft;

      // Continue animation frame if still scrolling
      scrollState.current.frame = requestAnimationFrame(tick);
    };

    tick();
  }, []);

  const stopScrolling = useCallback(() => {
    if (scrollState.current.frame) {
      cancelAnimationFrame(scrollState.current.frame);
      scrollState.current.frame = null;
    }
    scrollState.current.scaleX = 0;
  }, []);

  const handleDragStart = useCallback((index: number) => {
    setIsDragging(true);
    setDragIndex(index);
    setOriginalDragIndex(index);
    startScrolling();
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && originalDragIndex !== null) {
      onChange(tempValue, originalDragIndex, dragIndex);
    }
    setIsDragging(false);
    setDragIndex(null);
    setOriginalDragIndex(null);
    stopScrolling();
  }, [dragIndex, originalDragIndex, onChange, tempValue, stopScrolling]);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (dragIndex === null || !containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX;

      // Calculate scroll strength
      const strength = calculateStrength(containerRect, mouseX);

      // Check if item is out of view
      const isItemOutOfView =
        mouseX < containerRect.left || mouseX > containerRect.right;

      // Adjust strength based on visibility
      scrollState.current.scaleX = isItemOutOfView ? strength * 2 : strength;

      // Start scrolling if needed
      if (strength && !scrollState.current.frame) {
        startScrolling();
      }

      // Handle item reordering
      const newValue = [...tempValue];
      const draggedItem = newValue[dragIndex];
      newValue.splice(dragIndex, 1);
      newValue.splice(index, 0, draggedItem);

      setTempValue(newValue);
      setDragIndex(index);
    },
    [dragIndex, tempValue, calculateStrength, startScrolling]
  );

  const getContentSortableProps = useCallback(
    () => ({
      style: {
        display: "flex",
        flexDirection: direction === "vertical" ? "column" : "row",
        gap: "8px",
        position: "relative",
        overflowX: direction === "horizontal" ? "auto" : "visible",
        overflowY: direction === "vertical" ? "auto" : "visible"
      },
      "data-sortable-container": true,
      "data-direction": direction,
      ref: containerRef
    }),
    [direction]
  );

  const getSortableItemProps = useCallback(
    (index: number): SortableItemProps => {
      const isDraggingItem = dragIndex === index;

      return {
        style: {
          cursor: "grab",
          opacity: isDraggingItem ? 0.5 : 1,
          transform: isDraggingItem ? "scale(1.05)" : "scale(1)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          zIndex: isDraggingItem ? 1 : 0
        },
        "data-index": index,
        "data-sortable": true,
        draggable: true,
        onDragStart: () => handleDragStart(index),
        onDragEnd: handleDragEnd,
        onDragOver: (e: React.DragEvent) => handleDragOver(e, index)
      };
    },
    [dragIndex, handleDragEnd, handleDragOver, handleDragStart]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScrolling();
    };
  }, [stopScrolling]);

  return {
    getContentSortableProps,
    getSortableItemProps,
    isDragging,
    dragIndex,
    containerRef
  };
}
